/**
 * Wraps a label on separator characters, never in the middle of a word.
 *
 * jsPDF's width wrap will split "FAZE-EXTENSION-KIT" into "FAZE-EXTENSI" and
 * "ON-KIT" once the SKU column is narrower than the string. Hyphens, slashes
 * and underscores are the only places a code like that is allowed to break.
 * A token that is itself wider than the column is cut only as a last resort.
 */
export function wrapLabel(
  text: string,
  maxWidth: number,
  measure: (value: string) => number,
): string {
  const source = text.trim();

  if (!source || maxWidth <= 0 || measure(source) <= maxWidth) {
    return source;
  }

  const pieces = source.split(/([-/_])/);
  const tokens: string[] = [];

  for (const piece of pieces) {
    if (!piece) continue;

    if (piece === '-' || piece === '/' || piece === '_') {
      if (tokens.length === 0) tokens.push(piece);
      else tokens[tokens.length - 1] += piece;
      continue;
    }

    tokens.push(piece);
  }

  const lines: string[] = [];
  let current = '';

  const pushOversized = (token: string) => {
    let rest = token;

    while (rest && measure(rest) > maxWidth) {
      let take = rest.length;

      while (take > 1 && measure(rest.slice(0, take)) > maxWidth) {
        take -= 1;
      }

      lines.push(rest.slice(0, take));
      rest = rest.slice(take);
    }

    return rest;
  };

  for (const token of tokens) {
    const next = current + token;

    if (measure(next) <= maxWidth) {
      current = next;
      continue;
    }

    if (current) lines.push(current);

    if (measure(token) <= maxWidth) {
      current = token;
      continue;
    }

    current = pushOversized(token);
  }

  if (current) lines.push(current);

  return lines.join('\n');
}
